import { Button, Card, CardContent, CircularProgress, IconButton, InputAdornment, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { CognitoIdentityServiceProvider as Cognito } from 'aws-sdk';
import React from 'react';
import { ValidatorForm } from 'react-form-validator-core';

import InputValidator from '@/components/input-validator';
import * as Icons from '@/icons';

const styles = (theme: Theme) =>
  createStyles({
    progress: {
      marginTop: theme.spacing(14),
    },
    loginCard: {
      [theme.breakpoints.down('xs')]: {
        height: '100%',
      },
    },
    cardPadding: {
      height: '100%',
      paddingRight: '2em',
      paddingLeft: '2em',
      paddingTop: '2em',
      [theme.breakpoints.up('sm')]: {
        width: '500px !important', // 580
        height: '580px !important', // 340
        paddingRight: '4em !important',
        paddingLeft: '4em !important',
      },
    },
    cardPaddingLoading: {
      height: '100%',
      paddingRight: '2em',
      paddingLeft: '2em',
      paddingTop: '2em',
      [theme.breakpoints.up('sm')]: {
        width: '500px !important', // 580
        height: '370px !important', // 340
        paddingRight: '4em !important',
        paddingLeft: '4em !important',
      },
    },
    textFields: {
      width: '100%',
    },
    heading: {
      textAlign: 'center',
    },
    containerClass: {
      width: '100%',
      display: 'block',
      textAlign: 'center',
      height: '100%',
    },
    checkmark: {
      width: '74px',
      display: 'block',
      margin: '0 auto',
      marginTop: theme.spacing(14),
    },
    checkmarkCircle: {
      strokeDasharray: 1000,
      strokeDashoffset: 0,
      animation: 'dash 0.9s ease-in-out',
    },
    checkmarkCheck: {
      strokeDasharray: 1000,
      strokeDashoffset: 1000,
      animation: 'dash-check 0.9s 0.35s ease-in-out forwards',
    },
    '@keyframes dash': {
      '0%': {
        strokeDashoffset: 1000,
      },
      '100%': {
        strokeDashoffset: 0,
      },
    },
    '@keyframes dash-check': {
      '0%': {
        strokeDashoffset: 1000,
      },
      '100%': {
        strokeDashoffset: 100,
      },
    },
  });

interface IMyProps {
  username: string;
  password: string;
  challengeName: string | null;
  requiredAttributes?: any;
  onSubmit: any;
  onChangePass: any;
  onChangeUser: any;
  session: string | null;
  isLoading: boolean;
  isSuccessful: boolean;
  passwordPolicy: Cognito.PasswordPolicyType;
  type: 'RESET' | 'NEW';
}

interface IMyState {
  newpassword: string;
  newpassword2: string;
  showOldPassword: boolean;
  showNewPassword: boolean;
  showNewPassword2: boolean;
  attributes: any;
}

class NewPassword extends React.Component<IMyProps & WithStyles<typeof styles>, IMyState> {
  readonly state: IMyState = {
    newpassword: '',
    newpassword2: '',
    showOldPassword: false,
    showNewPassword: false,
    showNewPassword2: false,
    attributes: {},
  };

  constructor(props: IMyProps & WithStyles<typeof styles>) {
    super(props);
    ValidatorForm.addValidationRule('isPasswordMatch', (value) => {
      if (value !== this.state.newpassword) {
        return false;
      }
      return true;
    });
  }

  onSubmit(e: any) {
    if (this.state.newpassword !== this.state.newpassword2) {
      return;
    }

    if (!this.props.challengeName) {
      return;
    }

    const payload = {
      username: this.props.username,
      password: this.state.newpassword,
      challengeName: this.props.challengeName,
      challengeCode: this.props.password, // On initial user creation
      session: this.props.session,
    };

    if (this.props.requiredAttributes && this.props.requiredAttributes.length) {
      this.props.requiredAttributes.map((att: keyof typeof payload) => {
        payload[att] = this.state.attributes[att];
      });
    }

    this.props.onSubmit(e, payload);
  }

  handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  handleClickShowPassword = (field: 'showOldPassword' | 'showNewPassword' | 'showNewPassword2') => {
    this.setState({ [field]: !this.state[field] } as any);
  };

  render() {
    const { classes, passwordPolicy } = this.props;

    const validators = ['required'];
    const validatorMessages = ['this field is required'];
    if (passwordPolicy.MinimumLength) {
      validators.push(`minStringLength:${passwordPolicy.MinimumLength}`);
      validatorMessages.push(`password must be atleast ${passwordPolicy.MinimumLength} characters long`);
    }
    if (passwordPolicy.RequireLowercase) {
      validators.push('matchRegexp:(?=.*[a-z])');
      validatorMessages.push('password must contain atleast one lower case character');
    }
    if (passwordPolicy.RequireUppercase) {
      validators.push('matchRegexp:(?=.*[A-Z])');
      validatorMessages.push('password must contain atleast one upper case character');
    }
    if (passwordPolicy.RequireNumbers) {
      validators.push('matchRegexp:(?=.*\\d)');
      validatorMessages.push('password must contain atleast one number character');
    }
    if (passwordPolicy.RequireSymbols) {
      validators.push(
        'matchRegexp:(.*[\\^\\$\\*\\.\\[\\]\\{\\}\\(\\)\\?\\-\\"\\!\\@\\#\\%\\&\\/\\,\\>\\<\\\'\\:\\;\\|\\_\\~\\=\\`].*)',
      );
      validatorMessages.push('password must contain atleast one symbol character');
    }

    let card = (
      <CardContent className={classes.cardPadding}>
        <ValidatorForm ref="form" method="post" onSubmit={(event: any) => this.onSubmit(event)}>
          <Typography className={classes.heading} variant="h5">
            {this.props.type === 'RESET' ? 'Reset password' : 'Change password'}
          </Typography>
          {this.props.type === 'RESET' ? (
            <Typography className={classes.heading}>
              Your password has been reset, please check your email for a verification code
            </Typography>
          ) : null}
          <br />
          <br />
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClass}
            name="username"
            label="Email"
            validators={['required', 'isEmail']}
            errorMessages={['this field is required', 'must be a valid email']}
            value={this.props.username}
            onChange={(e) => {
              this.props.onChangeUser(e.target.value);
            }}
          />
          <br />
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClass}
            name="password"
            label={this.props.type === 'NEW' ? 'Temporary password' : 'Verification code'}
            type={this.state.showOldPassword || this.props.type === 'RESET' ? 'text' : 'password'}
            value={this.props.password}
            validators={['required']}
            errorMessages={['this field is required']}
            onChange={(e) => {
              this.props.onChangePass(e.target.value);
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  tabIndex={-1}
                  onClick={() => this.handleClickShowPassword('showOldPassword')}
                  onMouseDown={this.handleMouseDownPassword}>
                  {this.state.showOldPassword ? <Icons.VisibilityOff /> : <Icons.Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <br />
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClass}
            name="newPassword"
            autoComplete="new-password"
            label="New password"
            type={this.state.showNewPassword ? 'text' : 'password'}
            value={this.state.newpassword}
            validators={validators}
            errorMessages={validatorMessages}
            onChange={(e) => this.setState({ newpassword: e.target.value })}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  tabIndex={-1}
                  onClick={() => this.handleClickShowPassword('showNewPassword')}
                  onMouseDown={this.handleMouseDownPassword}>
                  {this.state.showNewPassword ? <Icons.VisibilityOff /> : <Icons.Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <br />
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClass}
            name="newPassword2"
            autoComplete="new-password"
            label="Repeat new password"
            type={this.state.showNewPassword2 ? 'text' : 'password'}
            value={this.state.newpassword2}
            validators={[...validators, 'isPasswordMatch']}
            errorMessages={[...validatorMessages, 'passwords must match']}
            onChange={(e) => this.setState({ newpassword2: e.target.value })}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  tabIndex={-1}
                  onClick={() => this.handleClickShowPassword('showNewPassword2')}
                  onMouseDown={this.handleMouseDownPassword}>
                  {this.state.showNewPassword2 ? <Icons.VisibilityOff /> : <Icons.Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <br />
          <br />
          <Button variant="contained" color="primary" type="submit">
            Submit
          </Button>
        </ValidatorForm>
      </CardContent>
    );

    if (this.props.isSuccessful) {
      card = (
        <CardContent className={classes.cardPaddingLoading}>
          <div className={classes.containerClass}>
            <Typography className={classes.heading} variant="h5">
              Successfully signed in!
            </Typography>
            <svg
              className={classes.checkmark}
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 130.2 130.2">
              <circle
                className={classes.checkmarkCircle}
                fill="none"
                stroke="#73AF55"
                strokeWidth="9"
                strokeMiterlimit="10"
                cx="65.1"
                cy="65.1"
                r="60.1"
              />
              <polyline
                className={classes.checkmarkCheck}
                fill="none"
                stroke="#73AF55"
                strokeWidth="9"
                strokeLinecap="round"
                strokeMiterlimit="10"
                points="100.2,40.2 51.5,88.8 29.8,67.5 "
              />
            </svg>
          </div>
        </CardContent>
      );
    } else if (this.props.isLoading) {
      card = (
        <CardContent className={classes.cardPaddingLoading}>
          <div className={classes.containerClass}>
            <Typography className={classes.heading} variant="h5">
              Signing you in...
            </Typography>
            <CircularProgress className={classes.progress} size={80} color="secondary" />
          </div>
        </CardContent>
      );
    }

    return <Card className={classes.loginCard}>{card}</Card>;
  }
}

export default withStyles(styles)(NewPassword);
